import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard, Loader2, CheckCircle2, AlertTriangle, Receipt, ShieldCheck,
  Clock, CalendarClock, Ban, RefreshCw, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { PLAN_LIST, planById } from "@/components/pricingData";
import { SquareCardForm } from "@/components/dashboard/SquareCardForm";
import { toast } from "sonner";

const STATUS_META = {
  none: { label: "Not started", cls: "bg-slate-100 text-slate-600" },
  payment_verified: { label: "Ready — awaiting activation", cls: "bg-indigo-100 text-indigo-700" },
  trialing: { label: "Free trial", cls: "bg-blue-100 text-blue-700" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  past_due: { label: "Payment retrying", cls: "bg-amber-100 text-amber-700" },
  unpaid: { label: "Suspended", cls: "bg-red-100 text-red-700" },
  trial_expired: { label: "Trial ended", cls: "bg-red-100 text-red-700" },
  canceled: { label: "Cancelled", cls: "bg-slate-200 text-slate-600" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const money = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [planPicker, setPlanPicker] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([api.get("/billing/status"), api.get("/billing/history")]);
    setStatus(s.data); setHistory(h.data.invoices || []);
    setSelectedPlan((p) => p || s.data.plan);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (params.get("billing") === "verified") { toast.success("Payment method verified"); setParams({}, { replace: true }); load(); }
    else if (params.get("billing") === "failed") { toast.error("Verification failed"); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const verifyCardToken = async (token) => {
    setBusy(true);
    try {
      await api.post("/billing/verify-card", { plan: selectedPlan || status.plan, token });
      toast.success("Payment method verified — £1 charged");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not verify your card"); }
    finally { setBusy(false); }
  };
  const updateCardToken = async (token) => {
    setBusy(true);
    try {
      await api.post("/billing/update-payment-method", { token });
      toast.success("Payment method updated");
      setUpdateOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update your card"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Your AI receptionist will stop at the end of the current period.")) return;
    try { await api.post("/billing/cancel"); toast.success("Subscription cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  if (!status) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading billing…</div>;

  const meta = STATUS_META[status.status] || STATUS_META.none;
  const plan = planById(status.plan) || status.plan_details;
  const needsCard = !status.card_verified;

  return (
    <div data-testid="billing-view" className="max-w-4xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Billing &amp; subscription</h1>
          <p className="text-slate-500 mt-1">Manage your plan, payment method and invoices.</p>
        </div>
        <span data-testid="billing-status-badge" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${meta.cls}`}>{meta.label}</span>
      </div>

      {/* Suspended / past due banner */}
      {status.status === "unpaid" && (
        <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="suspended-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div><p className="font-semibold text-red-800">Your AI receptionist is suspended</p><p className="text-sm text-red-700">We couldn't collect payment after several attempts. Update your payment method to reactivate.</p></div>
        </div>
      )}
      {status.status === "past_due" && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="pastdue-banner">
          <RefreshCw className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div><p className="font-semibold text-amber-800">Payment failed — retrying automatically</p><p className="text-sm text-amber-700">We'll retry your card shortly. Please make sure your payment method is up to date.</p></div>
        </div>
      )}

      {/* Card verification gate */}
      {needsCard && (
        <div className="mt-5 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-5" data-testid="verify-card-panel">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-600" /><h3 className="font-semibold text-slate-900">Add a payment method to begin</h3></div>
          <p className="text-sm text-slate-600 mt-2">A valid card is required before your AI receptionist can go live. We take a <b>£1 verification charge</b>; your trial and plan only begin once your number is active.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {PLAN_LIST.map((pl) => (
              <button key={pl.id} data-testid={`billing-plan-${pl.id}`} onClick={() => setSelectedPlan(pl.id)} disabled={busy}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${(selectedPlan || status.plan) === pl.id ? "border-blue-600 bg-white ring-1 ring-blue-200" : "border-slate-200 bg-white hover:border-blue-300"}`}>
                {pl.name} · £{pl.price}/mo
              </button>
            ))}
          </div>
          <div className="mt-4 max-w-sm">
            {config ? (
              <SquareCardForm config={config} onToken={verifyCardToken} busy={busy} />
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading secure card form…</div>
            )}
          </div>
        </div>
      )}

      {/* Overview grid */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="billing-overview">
        <Cell icon={CreditCard} label="Selected plan" value={plan ? `${plan.name} · £${plan.price}/mo` : "—"} />
        <Cell icon={ShieldCheck} label="Payment provider" value={status.provider} />
        <Cell icon={CheckCircle2} label="Payment method" value={status.card?.last4 ? `${status.card.brand?.toUpperCase()} ••••${status.card.last4}` : (status.card_verified ? "On file" : "Not added")} />
        <Cell icon={Clock} label="Trial started" value={fmt(status.trial_started_at)} />
        <Cell icon={CalendarClock} label="Trial ends" value={fmt(status.trial_ends_at)} highlight={status.status === "trialing"} />
        <Cell icon={CalendarClock} label="Next billing date" value={fmt(status.next_billing_at)} />
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap gap-2">
        {status.card_verified && (
          <Button data-testid="update-payment-btn" variant="outline" onClick={() => setUpdateOpen(true)} disabled={busy} className="gap-2"><CreditCard className="h-4 w-4" /> Update payment method</Button>
        )}
        {["trialing", "active", "past_due"].includes(status.status) && (
          <Button data-testid="cancel-sub-btn" variant="outline" onClick={cancel} className="gap-2 text-red-600 hover:text-red-700"><Ban className="h-4 w-4" /> {status.status === "trialing" ? "End trial" : "Cancel subscription"}</Button>
        )}
      </div>

      {/* Update payment method dialog */}
      <Dialog open={updateOpen} onOpenChange={setUpdateOpen}>
        <DialogContent data-testid="update-card-dialog">
          <DialogHeader><DialogTitle>Update payment method</DialogTitle>
            <DialogDescription>Enter a new card. We'll securely save it with Square for your subscription charges.</DialogDescription>
          </DialogHeader>
          {config ? (
            <SquareCardForm config={config} onToken={updateCardToken} busy={busy} />
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Billing history */}
      <h2 className="mt-8 text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5" /> Billing history &amp; receipts</h2>
      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
        {history.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="history-empty">No invoices yet. Your £1 verification and subscription charges will appear here.</div>
        ) : (
          <table className="w-full text-sm" data-testid="history-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left font-semibold px-4 py-2.5">Date</th><th className="text-left font-semibold px-4 py-2.5">Description</th><th className="text-right font-semibold px-4 py-2.5">Amount</th><th className="text-left font-semibold px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((inv) => (
                <tr key={inv.id} data-testid={`history-row-${inv.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-500">{fmt(inv.created_at)}</td>
                  <td className="px-4 py-2.5 text-slate-700">{inv.description}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{money(inv.amount)}</td>
                  <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{inv.status}</span></td>
                  <td className="px-4 py-2.5 text-right">{inv.receipt_url && <a href={inv.receipt_url} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-1 text-xs font-medium"><Download className="h-3 w-3" /> Receipt</a>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Cell({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className="mt-1.5 font-semibold text-slate-900 text-sm">{value}</p>
    </div>
  );
}
